/**
 * Turns admin audit entries into the readable events of Overview › Recent
 * system activity. Only actions taken on this page count: a script applied,
 * a request moved, a flag changed, a repair. Anything else in the audit log
 * (a catalog product edit under `/normalization/canonical/...`, a dry run,
 * a user or organization edit) returns null and is left to the Audit Log.
 *
 * Settings saves are not audited by the middleware, so the Overview adds them
 * from the settings documents' own `updatedAt` / `updatedBy` (`settingsEvent`).
 */
import type { SignalTone } from '../../theme/tokens';
import { asDate } from './api';

export type ActivityArea = 'scripts' | 'requests' | 'settings' | 'flags' | 'repair';

export interface ActivityAuditEntry {
  id: string;
  path?: string;
  method?: string;
  actorEmail?: string | null;
  actorName?: string | null;
  actorId?: string;
  createdAt?: unknown;
  metadata?: Record<string, unknown> | null;
}

export interface ActivityEvent {
  id: string;
  area: ActivityArea;
  tone: SignalTone;
  /** Key under `admin.systemV2.activity.events`. */
  key: string;
  params: Record<string, string | number>;
  at: Date | null;
  actor: string | null;
}

const stripApi = (path: string): string => (path.split('?')[0] || '').replace(/^\/api(?=\/|$)/, '');

const decode = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const actorOf = (entry: ActivityAuditEntry): string | null =>
  entry.actorEmail || entry.actorName || entry.actorId || null;

const meta = (entry: ActivityAuditEntry, key: string): unknown =>
  entry.metadata && typeof entry.metadata === 'object' ? entry.metadata[key] : undefined;

type Mapped = Pick<ActivityEvent, 'area' | 'tone' | 'key' | 'params'>;

function flagEvent(flag: string, value: unknown, scoped: boolean): Mapped {
  const prefix = scoped ? 'flagOrg' : 'flag';
  if (value === true) return { area: 'flags', tone: 'positive', key: `${prefix}On`, params: { flag } };
  if (value === false) return { area: 'flags', tone: 'neutral', key: `${prefix}Off`, params: { flag } };
  if (value === 'plan') return { area: 'flags', tone: 'info', key: 'flagPlan', params: { flag } };
  if (value === null) return { area: 'flags', tone: 'neutral', key: `${prefix}Reset`, params: { flag } };
  // Entries written before the audit kept the value.
  return { area: 'flags', tone: 'info', key: `${prefix}Changed`, params: { flag } };
}

function privacyEvent(status: unknown): Mapped {
  if (status === 'in_progress') return { area: 'requests', tone: 'info', key: 'privacyStarted', params: {} };
  if (status === 'completed') return { area: 'requests', tone: 'positive', key: 'privacyCompleted', params: {} };
  if (status === 'denied') return { area: 'requests', tone: 'problem', key: 'privacyDenied', params: {} };
  return { area: 'requests', tone: 'info', key: 'privacyUpdated', params: {} };
}

/**
 * The event an audit entry stands for, or null when it is not a system action.
 * `releaseStepTitles` names a release step by its id when the list is loaded.
 */
export function describeAuditEntry(
  entry: ActivityAuditEntry,
  releaseStepTitles: Record<string, string> = {},
): Mapped | null {
  const path = stripApi(entry.path || '');
  const method = (entry.method || '').toUpperCase();
  let match: RegExpMatchArray | null;

  if (method === 'PUT' && (match = path.match(/^\/admin\/feature-flags\/organizations\/[^/]+\/([^/]+)\/?$/))) {
    return flagEvent(decode(match[1]), meta(entry, 'flagValue'), true);
  }
  if (method === 'PUT' && (match = path.match(/^\/admin\/feature-flags\/([^/]+)\/?$/))) {
    return flagEvent(decode(match[1]), meta(entry, 'flagValue'), false);
  }
  if (method === 'POST' && (match = path.match(/^\/admin\/release-steps\/([^/]+)\/apply\/?$/))) {
    const id = decode(match[1]);
    return { area: 'scripts', tone: 'positive', key: 'releaseApplied', params: { name: releaseStepTitles[id] || id } };
  }
  if (method === 'POST' && (match = path.match(/^\/normalization\/process(?:\/brand\/([^/]+)|\/([^/]+))?\/?$/))) {
    // `/process/single` is one product, not a script run.
    if (match[2] === 'single') return null;
    const dryRun = meta(entry, 'dryRun');
    if (dryRun === true) return null;
    const target = match[1] ? decode(match[1]) : match[2] ? decode(match[2]) : '';
    const applied = dryRun === false;
    if (match[1]) {
      return { area: 'scripts', tone: applied ? 'positive' : 'info', key: applied ? 'importApplied' : 'importRan', params: { target } };
    }
    return {
      area: 'scripts',
      tone: applied ? 'positive' : 'info',
      key: applied ? (target ? 'normalizeCollectionApplied' : 'normalizeApplied') : target ? 'normalizeCollectionRan' : 'normalizeRan',
      params: { target },
    };
  }
  if (method === 'POST' && /^\/admin\/lens-focal-range\/backfill\/?$/.test(path)) {
    if (meta(entry, 'dryRun') !== false) return null;
    const count = meta(entry, 'changeCount');
    return typeof count === 'number'
      ? { area: 'scripts', tone: 'positive', key: 'lensAppliedCount', params: { count } }
      : { area: 'scripts', tone: 'positive', key: 'lensApplied', params: {} };
  }
  if (method === 'POST' && /^\/normalization\/compatibility\/run\/?$/.test(path)) {
    // `{ productId }` recomputes one product, which is not a sweep.
    const changed = meta(entry, 'changedFields');
    if (Array.isArray(changed) && changed.includes('productId')) return null;
    return { area: 'scripts', tone: 'info', key: 'compatibilityRan', params: {} };
  }
  if (method === 'PUT' && /^\/admin\/privacy-requests\/[^/]+\/status\/?$/.test(path)) {
    return privacyEvent(meta(entry, 'privacyStatus'));
  }
  if (method === 'POST' && /^\/admin\/privacy-requests\/[^/]+\/fulfill\/?$/.test(path)) {
    return privacyEvent('completed');
  }
  if (method === 'POST' && /^\/admin\/partner-slack\/requests\/[^/]+\/resolve\/?$/.test(path)) {
    const status = meta(entry, 'slackStatus');
    if (status === 'invited') return { area: 'requests', tone: 'positive', key: 'slackInvited', params: {} };
    if (status === 'dismissed') return { area: 'requests', tone: 'neutral', key: 'slackDismissed', params: {} };
    return { area: 'requests', tone: 'info', key: 'slackClosed', params: {} };
  }
  if (method === 'POST' && (match = path.match(/^\/admin\/repair\/user-org\/([^/]+)\/fix\/?$/))) {
    const action = meta(entry, 'repairAction');
    const user = decode(match[1]);
    if (action === 'remove_membership' || action === 'set_primary' || action === 'link_organization') {
      return { area: 'repair', tone: 'positive', key: `repair_${action}`, params: { user } };
    }
    return { area: 'repair', tone: 'positive', key: 'repairFixed', params: { user } };
  }
  if (method === 'POST' && (match = path.match(/^\/admin\/repair\/user-org\/([^/]+)\/?$/))) {
    return { area: 'repair', tone: 'positive', key: 'repair_link_organization', params: { user: decode(match[1]) } };
  }
  return null;
}

/** A settings document's last save, as an event. */
export function settingsEvent(
  id: string,
  key: string,
  updatedAt: unknown,
  updatedBy: string | null | undefined,
  params: Record<string, string | number> = {},
): ActivityEvent | null {
  const at = asDate(updatedAt);
  if (!at) return null;
  return { id, area: 'settings', tone: 'positive', key, params, at, actor: updatedBy || null };
}

/**
 * The newest `limit` events from audit entries plus any extra events, newest
 * first, each audit entry once.
 */
export function buildActivity(
  entries: ActivityAuditEntry[],
  extra: Array<ActivityEvent | null>,
  releaseStepTitles: Record<string, string> = {},
  limit = 6,
): ActivityEvent[] {
  const seen = new Set<string>();
  const events: ActivityEvent[] = [];
  for (const entry of entries) {
    if (!entry?.id || seen.has(entry.id)) continue;
    seen.add(entry.id);
    const mapped = describeAuditEntry(entry, releaseStepTitles);
    if (!mapped) continue;
    events.push({ id: entry.id, ...mapped, at: asDate(entry.createdAt), actor: actorOf(entry) });
  }
  for (const event of extra) if (event) events.push(event);
  events.sort((a, b) => (b.at?.getTime() || 0) - (a.at?.getTime() || 0));
  return events.slice(0, limit);
}
